import React from "react";
import "@/App.css";
import "@/styles/premium.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Protected from "@/components/common";
import DashboardLayout from "@/components/DashboardLayout";
import Landing from "@/pages/Landing";
import { LoginPage, RegisterPage } from "@/pages/Auth";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/PasswordReset";
import BuyerDashboard from "@/pages/buyer/BuyerDashboard";
import RfqCreate from "@/pages/buyer/RfqCreate";
import RfqList from "@/pages/buyer/RfqList";
import RfqDetail from "@/pages/RfqDetail";
import SupplierDashboard from "@/pages/supplier/SupplierDashboard";
import SupplierInbox from "@/pages/supplier/SupplierInbox";
import Products from "@/pages/supplier/Products";
import OrdersPage from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import CompanyPage from "@/pages/Company";
import AnalyticsPage from "@/pages/Analytics";
import BrowseProducts from "@/pages/buyer/BrowseProducts";
import TicketsPage from "@/pages/Tickets";
import AuditPage from "@/pages/Audit";
import BillingPage from "@/pages/Billing";
import AdminMonetization from "@/pages/AdminMonetization";
import { AdminOverview, AdminVerifications, AdminVerificationDetail, AdminAllRfqs, AdminAllOrders } from "@/pages/Admin";
import QuoteCompare from "@/pages/buyer/QuoteCompare";
import DocumentVault from "@/pages/DocumentVault";
import NotificationCenter from "@/pages/NotificationCenter";
import TeamPage from "@/pages/Team";
import AcceptInvite from "@/pages/AcceptInvite";
import PublicLegal from "@/pages/public/PublicLegal";
import { PublicBlogIndex, PublicBlogPost } from "@/pages/public/PublicBlog";
import AdminContent from "@/pages/AdminContent";

function RoleDashboard() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "buyer") return <BuyerDashboard />;
  if (user.role === "supplier") return <SupplierDashboard />;
  return <AdminOverview />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/team/accept" element={<AcceptInvite />} />

          {/* Public legal pages (admin-managed) */}
          <Route path="/terms" element={<PublicLegal />} />
          <Route path="/privacy" element={<PublicLegal />} />
          <Route path="/data-handling" element={<PublicLegal />} />
          <Route path="/cookies" element={<PublicLegal />} />

          {/* Public blog */}
          <Route path="/blog" element={<PublicBlogIndex />} />
          <Route path="/blog/:slug" element={<PublicBlogPost />} />

          <Route path="/app" element={<Protected><DashboardLayout /></Protected>}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<RoleDashboard />} />
            <Route path="company" element={<CompanyPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="analytics" element={<AnalyticsPage />} />

            {/* Buyer */}
            <Route path="rfqs" element={<Protected roles={["buyer"]}><RfqList /></Protected>} />
            <Route path="rfqs/new" element={<Protected roles={["buyer"]}><RfqCreate /></Protected>} />
            <Route path="browse" element={<Protected roles={["buyer"]}><BrowseProducts /></Protected>} />

            {/* Shared RFQ detail (buyer + supplier + admin) */}
            <Route path="rfqs/:id" element={<Protected roles={["buyer", "supplier", "admin"]}><RfqDetail /></Protected>} />
            <Route path="rfqs/:id/compare" element={<Protected roles={["buyer"]}><QuoteCompare /></Protected>} />

            {/* Document Vault (whole-company; admin sees all) */}
            <Route path="documents" element={<DocumentVault />} />

            {/* Notifications (Item 8) */}
            <Route path="notifications" element={<NotificationCenter />} />

            {/* Team (Item 10) */}
            <Route path="team" element={<TeamPage />} />

            {/* Tickets */}
            <Route path="tickets" element={<TicketsPage />} />

            {/* Billing */}
            <Route path="billing" element={<BillingPage />} />

            {/* Supplier */}
            <Route path="inbox" element={<Protected roles={["supplier"]}><SupplierInbox /></Protected>} />
            <Route path="products" element={<Protected roles={["supplier"]}><Products /></Protected>} />

            {/* Admin */}
            <Route path="verifications" element={<Protected roles={["admin"]}><AdminVerifications /></Protected>} />
            <Route path="verifications/:id" element={<Protected roles={["admin"]}><AdminVerificationDetail /></Protected>} />
            <Route path="all-rfqs" element={<Protected roles={["admin"]}><AdminAllRfqs /></Protected>} />
            <Route path="all-orders" element={<Protected roles={["admin"]}><AdminAllOrders /></Protected>} />
            <Route path="audit" element={<Protected roles={["admin"]}><AuditPage /></Protected>} />
            <Route path="monetization" element={<Protected roles={["admin"]}><AdminMonetization /></Protected>} />
            <Route path="content" element={<Protected roles={["admin"]}><AdminContent /></Protected>} />
            <Route path="users" element={<Protected roles={["admin"]}><AdminVerifications /></Protected>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
